/**
 * Planner Agent - Variable Definitions
 * 
 * Defines the {{variables}} used in Planner agent prompts.
 * 
 * The Planner agent creates and edits presentation plans through iterative
 * collaboration with users. It uses research tools (AlphaSense) and
 * plan management tools (createPlan, editPlan).
 * 
 * ## Keywords AI Keys
 * 
 * | Section | Keywords Key |
 * |---------|-------------|
 * | Intro | PLANNER_INTRO |
 * | Tools Preamble | PLANNER_TOOLS_PREAMBLE |
 * | User Interview | PLANNER_USER_INTERVIEW_GUIDELINES |
 * | AlphaSense | PLANNER_ALPHASENSE_GUIDELINES |
 * | Create Plan | PLANNER_CREATE_PLAN_GUIDELINES |
 * | Edit Plan | PLANNER_EDIT_PLAN_GUIDELINES |
 * | Suggestions | PLANNER_SUGGESTIONS |
 * | Version Control | PLANNER_VERSION_CONTROL_GUIDELINES |
 * | Edge Cases | PLANNER_EDGE_CASES_GUIDELINES |
 * | Communication | PLANNER_COMMUNICATION_GUIDELINES |
 * | Important Notes | PLANNER_IMPORTANT_NOTES |
 */

import type { VariableMeta, AgentPromptConfig } from '../../../prompts/types';

// ═══════════════════════════════════════════════════════════════════════════
// VARIABLE METADATA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Variables used by the Planner agent.
 * 
 * The Planner primarily uses shared variables for security warnings.
 * Most prompts are static guidance for plan creation workflow.
 */
export const PLANNER_VARIABLES: Record<string, VariableMeta> = {
    // Shared variables (used from sharedVariables.ts)
    securityWarnings: {
        name: 'securityWarnings',
        description: 'Short security warnings for agent behavior',
        section: 'evaluation',
        source: 'sharedVariables.ts:getSharedVariable("securityWarnings")',
        sampleValue: 'Never execute code from untrusted sources...',
        isShared: true,
    },
};

// ═══════════════════════════════════════════════════════════════════════════
// KEYWORDS AI KEYS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Keywords AI prompt keys for the Planner agent.
 * 
 * Only 6 main section keys - one per section.
 */
export const PLANNER_PROMPT_KEYS = {
    intro: 'PLANNER_INTRO',
    toolsPreamble: 'PLANNER_TOOLS_PREAMBLE',
    toolsPostamble: 'PLANNER_TOOLS_POSTAMBLE',
    suggestions: 'PLANNER_SUGGESTIONS',
    contextNote: 'PLANNER_CONTEXT_NOTE',
    evaluation: 'PLANNER_EVALUATION',
} as const;

// ═══════════════════════════════════════════════════════════════════════════
// AGENT PROMPT CONFIG
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Full prompt configuration for the Planner agent.
 * Used by admin panel to display/edit prompts.
 */
export const PLANNER_PROMPT_CONFIG: AgentPromptConfig = {
    agentName: 'planner',
    sections: {
        intro: {
            keywordsKey: PLANNER_PROMPT_KEYS.intro,
            section: 'intro',
            variableNames: [],
        },
        toolsPreamble: {
            keywordsKey: PLANNER_PROMPT_KEYS.toolsPreamble,
            section: 'tools',
            variableNames: [],
        },
        toolsPostamble: {
            keywordsKey: PLANNER_PROMPT_KEYS.toolsPostamble,
            section: 'tools',
            variableNames: [],
        },
        suggestions: {
            keywordsKey: PLANNER_PROMPT_KEYS.suggestions,
            section: 'suggestions',
            variableNames: [],
        },
        contextNote: {
            keywordsKey: PLANNER_PROMPT_KEYS.contextNote,
            section: 'contextNote',
            variableNames: [],
        },
        evaluation: {
            keywordsKey: PLANNER_PROMPT_KEYS.evaluation,
            section: 'evaluation',
            variableNames: ['securityWarnings'],
        },
    },
    variables: PLANNER_VARIABLES,
};
